'use client';

// ============================================================================
// useWorkflowTemplateEnabled Hook
// Client-safe entitlement check: is an operational workflow template enabled
// for the current client (IndustrySolutionSettings → client_operational_
// workflow_instances.enabled)? Fails closed — returns enabled=false while
// loading or on fetch error.
//
// Unlike useIndustrySolutionActive (which keys on the *solution* the client
// activated), this keys on the *workflow* — so a capability shared between
// solutions (e.g. digital-voting under Real Estate & Property Management)
// lights up regardless of which solution enabled it.
// ============================================================================

import { useEffect, useState } from 'react';
import { authService } from '@/lib/authService';

export const DIGITAL_VOTING_TEMPLATE_KEY = 'digital-voting';

export interface WorkflowTemplateEnabledResult {
  /** True only once enabled state has loaded and the template is active. */
  enabled: boolean;
  /** True while the workflow-templates request is in flight. */
  loading: boolean;
}

export function useWorkflowTemplateEnabled(templateKey: string): WorkflowTemplateEnabledResult {
  const [state, setState] = useState<WorkflowTemplateEnabledResult>({ enabled: false, loading: true });

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const accessToken = authService.getTokens()?.access_token;
        if (!accessToken) return;
        const res = await fetch(
          `/api/v1/workflow-templates?template_key=${encodeURIComponent(templateKey)}`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        if (!res.ok) return;
        const data = await res.json();
        const template = Array.isArray(data?.templates) ? data.templates[0] : null;
        if (!cancelled) {
          setState({ enabled: Boolean(template?.is_active), loading: false });
        }
      } finally {
        if (!cancelled) {
          setState((prev) => (prev.loading ? { enabled: false, loading: false } : prev));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [templateKey]);

  return state;
}
